import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import { Box } from '@mui/material';
import '@aws-amplify/ui-react/styles.css';

import Layout from './components/Layout';
import Overview from './pages/Overview';
import Terminals from './pages/Terminals';
import TerminalDetails from './pages/TerminalDetails';
import TerminalTransactions from './pages/TerminalTransactions';
import Locations from './pages/Locations';
import LocationDetails from './pages/LocationDetails';
import ReconciliationPage from './pages/ReconciliationPage';
import SettlementReports from './pages/SettlementReports';
import PartnerSurcharge from './pages/PartnerSurcharge';
import Upload from './pages/Upload';
import UserManagement from './pages/UserManagement';
import SftpManagement from './pages/SftpManagement';
import AtmServerManagement from './pages/AtmServerManagement';
import PublicTransactionSearch from './pages/PublicTransactionSearch';
import BrandShell from './onboarding/BrandShell';
import InvitePage from './onboarding/pages/InvitePage';
import WizardPage from './onboarding/pages/WizardPage';
import StatusPage from './onboarding/pages/StatusPage';
import ApplyStartPage from './onboarding/pages/ApplyStartPage';
import ContinuePage from './onboarding/pages/ContinuePage';
import OwnerBankingPage from './onboarding/pages/OwnerBankingPage';
import OnboardingAdmin from './pages/OnboardingAdmin';
import OnboardingApplication from './pages/OnboardingApplication';
import PaiDashboard from './pages/PaiDashboard';
import TransactionTroubleshooting from './pages/TransactionTroubleshooting';
import SettlementBatches from './pages/SettlementBatches';
import ScheduledReports from './pages/ScheduledReports';
import Channels from './pages/Channels';
import Profile from './pages/Profile';
import { AuthProvider } from './contexts/AuthContext';
import { ApiProvider } from './contexts/ApiContext';

const formFields = {
  signIn: {
    username: {
      labelHidden: false,
      placeholder: 'Enter your email address',
      isRequired: true,
      label: 'Email'
    }
  }
};

const components = {
  Header() {
    return (
      <Box sx={{ textAlign: 'center', padding: 2 }}>
        <img
          alt="Reporting Portal"
          src="/logo.png"
          style={{ width: '200px', height: 'auto' }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <h1>
          <span style={{
            fontFamily: 'Allura, cursive',
            fontSize: '2.5rem',
            letterSpacing: '2px',
            fontWeight: 400,
            marginRight: '8px'
          }}>
            MyView
          </span>
          Reporting Portal
        </h1>
      </Box>
    );
  }
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/public/search" element={<PublicTransactionSearch />} />

        {/* Merchant onboarding — public, branded, no portal login (spec §7) */}
        {/* Open link (2026-10-30): agent code → generic application; continue link; lost-link form */}
        <Route path="/apply/continue/:token" element={<BrandShell brandSlug="generic"><ContinuePage /></BrandShell>} />
        <Route path="/apply/:code" element={<BrandShell brandSlug="generic"><ApplyStartPage /></BrandShell>} />
        <Route path="/apply" element={<BrandShell brandSlug="generic"><ApplyStartPage /></BrandShell>} />
        <Route path="/owner/:token" element={<BrandShell brandSlug="generic"><OwnerBankingPage /></BrandShell>} />
        <Route path="/:brand/onboarding/invite/:token" element={<BrandShell><InvitePage /></BrandShell>} />
        <Route path="/:brand/onboarding/app" element={<BrandShell><WizardPage /></BrandShell>} />
        <Route path="/:brand/onboarding/status" element={<BrandShell><StatusPage /></BrandShell>} />

        {/* Protected routes - require authentication */}
        <Route path="/*" element={
          <Authenticator
            formFields={formFields}
            components={components}
            hideSignUp={true}
          >
            {({ signOut, user }) => {
              // Wrap signOut to log when it's called
              const wrappedSignOut = () => {
                console.log('[APP] signOut called at', new Date().toLocaleTimeString());
                console.trace('signOut call stack:');
                signOut();
              };
              
              return (
                <AuthProvider user={user} signOut={wrappedSignOut}>
                  <ApiProvider>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Navigate to="/overview" replace />} />
                        <Route path="/overview" element={<Overview />} />
                        <Route path="/terminals" element={<Terminals />} />
                        <Route path="/terminals/:id" element={<TerminalDetails />} />
                        <Route path="/terminals/:id/transactions" element={<TerminalTransactions />} />
                        <Route path="/locations" element={<Locations />} />
                        <Route path="/locations/:id" element={<LocationDetails />} />
                        <Route path="/locations/:locationId/reconciliation" element={<ReconciliationPage />} />
                        <Route path="/settlement-reports" element={<SettlementReports />} />
                        <Route path="/partner-surcharge" element={<PartnerSurcharge />} />
                        <Route path="/settlement-batches" element={<SettlementBatches />} />
                        <Route path="/scheduled-reports" element={<ScheduledReports />} />
                        <Route path="/upload" element={<Upload />} />
                        <Route path="/users" element={<UserManagement />} />
                        <Route path="/sftp" element={<SftpManagement />} />
                        <Route path="/atm-servers" element={<AtmServerManagement />} />
                        <Route path="/pai" element={<PaiDashboard />} />
                        <Route path="/troubleshooting" element={<TransactionTroubleshooting />} />
                        <Route path="/channels" element={<Channels />} />
                        <Route path="/onboarding" element={<OnboardingAdmin />} />
                        <Route path="/onboarding/applications/:id" element={<OnboardingApplication />} />
                        <Route path="/profile" element={<Profile />} />
                        {/* Catch-all route for invalid URLs */}
                        <Route path="*" element={<Navigate to="/overview" replace />} />
                      </Routes>
                    </Layout>
                  </ApiProvider>
                </AuthProvider>
              )
            }}
          </Authenticator>
        } />
      </Routes>
    </Router>
  );
}

export default App;